import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trainings } from '../data/trainings';
import {
  Monitor,
  Users,
  Mic,
  Radio,
  Wrench,
  Building,
  ChevronRight,
  Shield,
  Code,
  Lock,
  ArrowRight,
  Github,
  Twitter,
  Linkedin,
  Zap,
  Tag
} from 'lucide-react';
import EventBanner from '../components/EventBanner';
import ServiceCard from '../components/ServiceCard';
import TrainingsTeaser from '../components/TrainingsTeaser';
import CountdownTimer from '../components/CountdownTimer';
import MobileStickyCTA from '../components/MobileStickyCTA';
import { EVENT_DATE_LABEL, EARLY_BIRD_DEADLINE } from '../data/eventConfig';
import { useInView } from '../hooks/useInView';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const HomePage = () => {
  const [typedText, setTypedText] = useState('');
  const [currentStatIndex, setCurrentStatIndex] = useState(0);
  const [hoveredService, setHoveredService] = useState(null);
  const fullText = 'Byt3con Academy';

  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index <= fullText.length) {
        setTypedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, []);

  const services = [
    {
      icon: Monitor,
      title: 'Quarterly Trainings',
      description: 'Intensive instructor-led training programs hosted quarterly. Available online or at designated venues with hands-on labs and expert mentorship.',
      features: ['Instructor-Led', 'Hands-on Labs', 'Expert Mentorship'],
      path: '/services/quarterly-trainings',
    },
    {
      icon: Users,
      title: 'Co-horts',
      description: 'Join intensive cohort-based programs with live instruction, peer learning, and mentorship from industry experts.',
      features: ['12-Week Programs', 'Live Sessions', 'Career Support'],
      path: '/services/cohorts',
    },
    {
      icon: Mic,
      title: 'Byt3cast Podcast',
      description: 'Weekly insights from security researchers, ethical hackers, and industry leaders on the latest threats and trends.',
      features: ['Weekly Episodes', 'Expert Guests', 'CTF Walkthroughs'],
      path: '/services/podcast',
    },
    {
      icon: Radio,
      title: 'Live Sessions',
      description: 'Interactive live workshops and Q&A sessions where you can learn directly from experienced security professionals.',
      features: ['Real-time Q&A', 'Demo Sessions', 'Recorded Access'],
      path: '/services/live-sessions',
    },
    {
      icon: Wrench,
      title: 'Tool Demos',
      description: 'Deep-dive demonstrations of the latest hacking tools and defensive technologies used by professionals.',
      features: ['Tool Reviews', 'Setup Guides', 'Best Practices'],
      path: '/services/tool-demos',
    },
    {
      icon: Building,
      title: 'Corporate Training',
      description: 'Customized security awareness and technical training programs designed for enterprise teams and organizations.',
      features: ['Custom Curriculum', 'On-site Options', 'Team Analytics'],
      path: '/services/corporate',
    },
  ];

  const stats = [
    { number: '5000+', label: 'Students Trained' },
    { number: '150+', label: 'Hours of Content' },
    { number: '98%', label: 'Success Rate' },
    { number: '50+', label: 'Industry Partners' },
  ];

  const terminalServices = [
    { label: 'Quarterly Trainings', path: '/services/quarterly-trainings' },
    { label: 'Co-horts ', path: '/services/cohorts' },
    { label: 'Byt3cast', path: '/services/podcast' },
    { label: 'Live Sessions', path: '/services/live-sessions' },
    { label: 'Corporate Trainings', path: '/services/corporate' },
    { label: 'Tool Demos', path: '/services/tool-demos' },
    { label: 'Study Groups', path: '/services/study-groups' },
  ];

  const testimonials = [
    {
      name: 'Participant',
      role: 'Byt3con June 2026 Attendee',
      content: 'The training platform was easy to access, and the lab setup was well organized. The step-by-step guidance provided throughout the session made it easy to follow the exercises and understand the concepts.',
      avatar: 'B3',
    },
    {
      name: 'Participant',
      role: 'Byt3con June 2026 Attendee',
      content: 'The overall event was good and managed smoothly without hurdles. Highly recommend Byt3con for anyone who are interested.',
      avatar: 'B3',
    },
  ];

  const faqs = [
    {
      question: 'What prerequisites do I need for your courses?',
      answer: 'Basic computer literacy and a passion for security are enough to start. We have courses for all skill levels, from complete beginners to advanced practitioners.',
    },
    {
      question: 'Do you offer job placement assistance?',
      answer: 'Yes! Our cohort programs include career support with resume reviews, interview prep, and connections to our network of 50+ hiring partners.',
    },
    {
      question: 'Are the certifications recognized in the industry?',
      answer: 'Our certificates are well-regarded and complement industry certifications like CompTIA Security+, CEH, and OSCP. Many students use our training to prepare for these exams.',
    },
    {
      question: 'Can I access course materials after completion?',
      answer: 'Absolutely. All enrolled students get lifetime access to course materials, including future updates and additions to the curriculum.',
    },
  ];

  const [whoForRef, whoForInView] = useInView();
  const [whyAttendRef, whyAttendInView] = useInView();

  const liveTrainings = trainings.filter(t => t.status === 'upcoming' || t.status === 'active');
  const threeDayTrainings = liveTrainings.filter(t => t.duration === '3 Days');
  const oneDayTrainings = liveTrainings.filter(t => t.duration === '1 Day');
  const twoDayTrainings = liveTrainings.filter(t => t.duration === '2 Days');
  const aiTrackCount = liveTrainings.filter(t => t.tags.some(tag => tag.toLowerCase().includes('ai'))).length;

  const trainerStrip = [];
  const seenTrainers = new Set();
  liveTrainings.forEach(training => {
    training.instructors.forEach(instructor => {
      if (!seenTrainers.has(instructor.name)) {
        seenTrainers.add(instructor.name);
        trainerStrip.push({ ...instructor, trainingId: training.id });
      }
    });
  });

  const communityPartners = [
    { name: 'BSides Ahmedabad', logo: '/images/community/BSidesAhm.webp', website: 'https://bsidesahmedabad.in/' },
    { name: 'BSides Bangalore', logo: '/images/community/bsides_bangalore_logo.webp', website: 'https://bsidesbangalore.in/' },
    { name: 'BSides Indore', logo: '/images/community/BSidesIndore.jpg', website: 'https://bsidesindore.in/' },
    { name: 'Haxination Mumbai', logo: '/images/community/Haxnation_Mumbai.webp', website: 'https://haxnation.org/mumbai-chapter/' },
    { name: 'BSides Mussorie', logo: '/images/community/Bsidesmussoorie_logo.webp', website: 'https://bsidesmussoorie.in/' },
    { name: 'Defcon Chennai', logo: '/images/community/Defcon_Chennai.webp', website: null },
  ];

  const team = [
    {
      name: 'Dhruv Shah',
      role: 'Co-Founder & Infosec Head',
      bio: '15+ years in offensive security Founder @ TCP Infosec. Former Director at Notsosecure Claranet.',
      avatar: '/images/DhruvShah.webp',
      social: {
        twitter: 'https://x.com/snypter',
        linkedin: 'https://www.linkedin.com/in/dhruvshahs',
        github: 'https://github.com/InfosecDhruv',
      },
    },
    {
      name: 'Harshit Mahajan',
      role: 'Co-Founder & Business Head',
      bio: '10+ years of experience in organizing Cybersecurity events.',
      avatar: '/images/harshitmahajan.webp',
      social: {
        twitter: 'https://x.com/HarshitRMahajan',
        linkedin: 'https://www.linkedin.com/in/harshitmahajan/',
        github: '#',
      },
    },
    {
      name: 'Anant Shrivastava',
      role: 'Advisor',
      bio: 'Founder @ Cyfinoid and and an expert in multiple domains of CyberSecurity.',
      avatar: '/images/AnantShri.webp',
      social: {
        twitter: 'https://x.com/anantshri',
        linkedin: 'https://www.linkedin.com/in/anantshri/',
        github: 'https://github.com/anantshri/',
      },
    },
  ];

  const strategicPartners = [
    { name: 'VULNCON', logo: '/images/community/VULNCON.webp', website: null },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Event Banner */}
      <EventBanner />

      {/* September 2026 Trainings Ticker Strip */}
      <div className="bg-[var(--surface)] border-b border-[var(--primary)] flex items-stretch overflow-hidden">
        {/* Label badge */}
        <div className="flex-shrink-0 bg-[var(--primary)] text-black font-mono font-bold text-xs uppercase tracking-widest px-3 flex items-center z-10">
          LIVE
        </div>

        {/* Scrolling track */}
        <div className="flex-1 overflow-hidden py-2">
          <div className="ticker-track">
            {[0, 1].map(i => (
              <span key={i} className="flex items-center gap-6 pr-16 font-mono text-sm">
                <span className="text-[var(--text-main)]">
                  <span className="text-[var(--primary)]">Byt3con Trainings 2026</span> · Sept 17–19 · Online
                </span>
                <span className="text-[var(--text-muted)]">◆</span>
                <span className="text-[var(--text-main)]">{trainings.filter(t => t.status === 'upcoming' || t.status === 'active').length} Trainings Available</span>
                <span className="text-[var(--text-muted)]">◆</span>
                <span className="text-[var(--text-main)]">{aiTrackCount} AI-Security Tracks</span>
                <span className="text-[var(--text-muted)]">◆</span>
                <span className="text-[var(--text-main)]">Attack, defend & use AI daily · Hands-on labs, not slides.</span>
                <span className="text-[var(--text-muted)]">◆</span>
              </span>
            ))}
          </div>
        </div>

        {/* CTA button */}
        <Link
          to="/services/quarterly-trainings"
          className="flex-shrink-0 font-mono text-xs bg-[var(--primary)] text-black font-bold uppercase tracking-widest border-l border-[var(--primary)] px-4 flex items-center gap-1 hover:bg-[var(--primary-dim)] transition-colors animate-pulse shadow-[0_0_8px_var(--primary)]"
        >
          View Trainings <ArrowRight className="w-3 h-3" style={{ animation: 'nudgeRight 1s ease-in-out infinite' }} />
        </Link>
      </div>

      {/* Trainer Strip */}
      <section className="py-10 border-b border-[var(--border)] bg-[var(--surface)]/40">
        <div className="container-main">
          <p className="text-center font-mono text-[var(--primary)] text-base uppercase tracking-widest mb-6">
            ./learn_from
          </p>
          <div className="flex flex-wrap items-start justify-center gap-x-8 gap-y-10">
            {trainerStrip.map((instructor, idx) => (
              <Link
                key={idx}
                to={`/services/quarterly-trainings/${instructor.trainingId}`}
                className="group flex flex-col items-center text-center gap-2 w-32"
              >
                <div className="w-28 h-28 rounded-full border-2 border-[var(--border)] group-hover:border-[var(--primary)] overflow-hidden bg-[var(--surface-highlight)] flex-shrink-0 transition-colors">
                  {instructor.photo
                    ? <img src={instructor.photo} alt={instructor.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    : <div className="w-full h-full flex items-center justify-center font-mono text-[var(--primary)] text-xl font-bold">
                        {instructor.name.split(' ').map(n => n[0]).join('')}
                      </div>
                  }
                </div>
                <div className="font-mono">
                  <p className="text-sm text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors leading-tight">{instructor.name}</p>
                  {(instructor.designation || instructor.company) && (
                    <p className="text-xs text-[var(--text-muted)] leading-tight mt-0.5">
                      {instructor.designation && instructor.company
                        ? `${instructor.designation} @ ${instructor.company}`
                        : instructor.designation || instructor.company}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-12 border-b border-[var(--border)]">
        <div className="container-main">
          <div className="text-center mb-8">
            <p className="font-mono text-[var(--primary)] text-xs sm:text-base uppercase tracking-widest mb-2">./who_this_is_for</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-main)]">
              Built for people who already do the work
            </h2>
            <p className="text-[var(--text-muted)] font-mono text-sm mt-2">If this sounds like you, you're in the right place.</p>
          </div>

          <div ref={whoForRef} className="max-w-2xl mx-auto space-y-3">
            {[
              'You write detection rules, not just read about them',
              "You've broken something in a sandbox on purpose",
              'You want labs that mirror production, not toy CTFs',
              "You'd rather run an attack chain than watch a slide about one",
              'You want a training that makes you better at your job — not just a certificate',
            ].map((item, i) => (
              <div
                key={item}
                className={`flex items-start gap-3 border border-[var(--border)] bg-[var(--surface-highlight)]/20 px-4 sm:px-5 py-3 ${whoForInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <span className="text-[var(--primary)] font-mono flex-shrink-0 mt-0.5">▸</span>
                <span className="font-mono text-sm sm:text-base text-[var(--text-main)]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Trainings Section */}
      <section className="pt-12 pb-12 relative overflow-hidden">
        <div className="hero-glow top-1/2 -right-64" />

        <div className="container-main relative z-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="font-mono text-[var(--primary)] text-xs sm:text-xl uppercase tracking-wider mb-1">
                BYT3CON Trainings September 2026 Online Edition
              </p>
              <h2 className="text-2xl font-bold text-[var(--text-main)]">Upcoming Trainings</h2>
            </div>
            <Link to="/services/quarterly-trainings" className="font-mono text-sm text-[var(--primary)] hover:underline flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {threeDayTrainings.length === 0 && oneDayTrainings.length === 0 && twoDayTrainings.length === 0 ? (
            <TrainingsTeaser />
          ) : (
          <div className="space-y-8">
            {/* 3-Day Trainings Group */}
            {threeDayTrainings.length > 0 && (
              <div>
                <p className="font-mono text-large uppercase tracking-widest text-[var(--text-muted)] mb-3 border-b border-[var(--border)] pb-2">
                  3-Day Trainings
                </p>
                <div className="space-y-4">
                  {threeDayTrainings.map(training => (
                    <Link
                      key={training.id}
                      to={`/services/quarterly-trainings/${training.id}`}
                      className="group flex flex-col sm:flex-row items-start gap-4 sm:gap-6 bg-[var(--background)] border-2 border-[var(--border)] hover:border-[var(--primary)] p-4 sm:p-6 transition-colors duration-200 overflow-hidden relative"
                    >
                      {/* Training Info */}
                      <div className="flex-1 min-w-0 pr-8 sm:pr-0">
                        <h3 className="font-mono font-bold text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors text-lg sm:text-xl leading-tight mb-2 sm:mb-2">
                          {training.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 mb-2 sm:mb-3">
                          <span className="px-2 py-1 sm:py-1.5 bg-[var(--surface-highlight)] border border-[var(--primary)] font-mono text-sm text-[var(--primary)] whitespace-nowrap">
                            {training.duration}
                          </span>
                          <span className="px-2 py-1 sm:py-1.5 bg-[var(--surface-highlight)] border border-[var(--primary)] font-mono text-sm text-[var(--primary)] whitespace-nowrap">
                            {training.venue}
                          </span>
                        </div>
                        <p className="text-base text-[var(--text-muted)] sm:line-clamp-2 leading-relaxed mb-2 sm:mb-3">
                          {training.description}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
                          {training.tags.map((tag, i) => (
                            <span key={i} className="px-2 py-1 sm:py-1.5 bg-[var(--surface-highlight)] border border-[var(--border)] font-mono text-sm text-[var(--text-muted)] whitespace-nowrap">
                              {tag}
                            </span>
                          ))}
                        </div>

                        <div className="space-y-2 mt-2 sm:mt-3">
                          {training.instructors.map((i, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-[var(--primary)] overflow-hidden bg-[var(--surface-highlight)] flex-shrink-0">
                                {i.photo
                                  ? <img src={i.photo} alt={i.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                  : <div className="w-full h-full flex items-center justify-center font-mono text-[var(--primary)] text-xs font-bold">
                                      {i.name.split(' ').map(n => n[0]).join('')}
                                    </div>
                                }
                              </div>
                              <div className="font-mono text-base min-w-0">
                                <p className="text-[var(--primary)]">{i.name}</p>
                                {(i.designation || i.company) && (
                                  <p className="text-sm text-[var(--text-muted)]">
                                    {i.designation && i.company
                                      ? `${i.designation} @ ${i.company}`
                                      : i.designation || i.company}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Navigation arrow */}
                      <ArrowRight className="absolute top-4 right-4 sm:static sm:flex-shrink-0 w-4 h-4 text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* 2-Day Trainings Group */}
            {twoDayTrainings.length > 0 && (
              <div>
                <p className="font-mono text-large uppercase tracking-widest text-[var(--text-muted)] mb-3 border-b border-[var(--border)] pb-2">
                  2-Day Trainings
                </p>
                <div className="space-y-4">
                  {twoDayTrainings.map(training => (
                    <Link
                      key={training.id}
                      to={`/services/quarterly-trainings/${training.id}`}
                      className="group flex flex-col sm:flex-row items-start gap-4 sm:gap-6 bg-[var(--background)] border-2 border-[var(--border)] hover:border-[var(--primary)] p-4 sm:p-6 transition-colors duration-200 overflow-hidden relative"
                    >
                      {/* Training Info */}
                      <div className="flex-1 min-w-0 pr-8 sm:pr-0">
                        <h3 className="font-mono font-bold text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors text-lg sm:text-xl leading-tight mb-2 sm:mb-2">
                          {training.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 mb-2 sm:mb-3">
                          <span className="px-2 py-1 sm:py-1.5 bg-[var(--surface-highlight)] border border-[var(--primary)] font-mono text-sm text-[var(--primary)] whitespace-nowrap">
                            {training.duration}
                          </span>
                          <span className="px-2 py-1 sm:py-1.5 bg-[var(--surface-highlight)] border border-[var(--primary)] font-mono text-sm text-[var(--primary)] whitespace-nowrap">
                            {training.venue}
                          </span>
                        </div>
                        <p className="text-base text-[var(--text-muted)] sm:line-clamp-2 leading-relaxed mb-2 sm:mb-3">
                          {training.description}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
                          {training.tags.map((tag, i) => (
                            <span key={i} className="px-2 py-1 sm:py-1.5 bg-[var(--surface-highlight)] border border-[var(--border)] font-mono text-sm text-[var(--text-muted)] whitespace-nowrap">
                              {tag}
                            </span>
                          ))}
                        </div>

                        <div className="space-y-2 mt-2 sm:mt-3">
                          {training.instructors.map((i, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-[var(--primary)] overflow-hidden bg-[var(--surface-highlight)] flex-shrink-0">
                                {i.photo
                                  ? <img src={i.photo} alt={i.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                  : <div className="w-full h-full flex items-center justify-center font-mono text-[var(--primary)] text-xs font-bold">
                                      {i.name.split(' ').map(n => n[0]).join('')}
                                    </div>
                                }
                              </div>
                              <div className="font-mono text-base min-w-0">
                                <p className="text-[var(--primary)]">{i.name}</p>
                                {(i.designation || i.company) && (
                                  <p className="text-sm text-[var(--text-muted)]">
                                    {i.designation && i.company
                                      ? `${i.designation} @ ${i.company}`
                                      : i.designation || i.company}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Navigation arrow */}
                      <ArrowRight className="absolute top-4 right-4 sm:static sm:flex-shrink-0 w-4 h-4 text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* 1-Day Trainings Group */}
            {oneDayTrainings.length > 0 && (
              <div>
                <p className="font-mono text-large uppercase tracking-widest text-[var(--text-muted)] mb-3 border-b border-[var(--border)] pb-2">
                  1-Day Trainings
                </p>
                <div className="space-y-4">
                  {oneDayTrainings.map(training => (
                    <Link
                      key={training.id}
                      to={`/services/quarterly-trainings/${training.id}`}
                      className="group flex flex-col sm:flex-row items-start gap-4 sm:gap-6 bg-[var(--background)] border-2 border-[var(--border)] hover:border-[var(--primary)] p-4 sm:p-6 transition-colors duration-200 overflow-hidden relative"
                    >
                      {/* Training Info */}
                      <div className="flex-1 min-w-0 pr-8 sm:pr-0">
                        <h3 className="font-mono font-bold text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors text-lg sm:text-xl leading-tight mb-2 sm:mb-2">
                          {training.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 mb-2 sm:mb-3">
                          <span className="px-2 py-1 sm:py-1.5 bg-[var(--surface-highlight)] border border-[var(--primary)] font-mono text-sm text-[var(--primary)] whitespace-nowrap">
                            {training.duration}
                          </span>
                          <span className="px-2 py-1 sm:py-1.5 bg-[var(--surface-highlight)] border border-[var(--primary)] font-mono text-sm text-[var(--primary)] whitespace-nowrap">
                            {training.venue}
                          </span>
                        </div>
                        <p className="text-base text-[var(--text-muted)] sm:line-clamp-2 leading-relaxed mb-2 sm:mb-3">
                          {training.description}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
                          {training.tags.map((tag, i) => (
                            <span key={i} className="px-2 py-1 sm:py-1.5 bg-[var(--surface-highlight)] border border-[var(--border)] font-mono text-sm text-[var(--text-muted)] whitespace-nowrap">
                              {tag}
                            </span>
                          ))}
                        </div>

                        <div className="space-y-2 mt-2 sm:mt-3">
                          {training.instructors.map((i, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-[var(--primary)] overflow-hidden bg-[var(--surface-highlight)] flex-shrink-0">
                                {i.photo
                                  ? <img src={i.photo} alt={i.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                  : <div className="w-full h-full flex items-center justify-center font-mono text-[var(--primary)] text-xs font-bold">
                                      {i.name.split(' ').map(n => n[0]).join('')}
                                    </div>
                                }
                              </div>
                              <div className="font-mono text-base min-w-0">
                                <p className="text-[var(--primary)]">{i.name}</p>
                                {(i.designation || i.company) && (
                                  <p className="text-sm text-[var(--text-muted)]">
                                    {i.designation && i.company
                                      ? `${i.designation} @ ${i.company}`
                                      : i.designation || i.company}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Navigation arrow */}
                      <ArrowRight className="absolute top-4 right-4 sm:static sm:flex-shrink-0 w-4 h-4 text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </section>

      {/* Corporate / Team CTA */}
      <section className="py-8 border-t border-[var(--border)] bg-[var(--surface)]/40">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
            <div>
              <p className="font-mono font-bold text-[var(--text-main)]">Training a Team?</p>
              <p className="font-mono text-sm text-[var(--text-muted)] mt-1">Get 10% off for 5 or more participants on any September training.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                to="/services/quarterly-trainings/register"
                className="bg-[var(--primary)] text-black font-mono font-bold text-sm px-6 py-3 text-center uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors"
              >
                See Team Pricing
              </Link>
              <Link
                to="/contact"
                className="border border-[var(--primary)] text-[var(--primary)] font-mono text-sm px-6 py-3 text-center uppercase tracking-widest hover:bg-[var(--surface-highlight)] transition-colors"
              >
                Talk to Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* September 2026 Event Highlights Section */}
      <section className="py-12 border-t border-[var(--border)]">
        <div className="container-main">
          <div className="text-center mb-8">
            <p className="font-mono text-[var(--primary)] text-base uppercase tracking-widest mb-2">./event_highlights</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-main)]">
              Why Attend <span className="text-[var(--primary)]">Byt3con</span> 2026?
            </h2>
            <p className="text-[var(--text-muted)] font-mono text-sm mt-2">Online, hands-on, expert-led security trainings.</p>
          </div>

          <div ref={whyAttendRef} className={`grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch mb-6 ${whyAttendInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            {/* Format card */}
            <div className="border border-[var(--primary)] p-5 bg-[var(--background)] flex flex-col gap-3">
              <p className="font-mono text-[var(--primary)] text-xs uppercase tracking-widest">Format</p>
              <h3 className="font-mono font-bold text-[var(--text-main)] text-lg">Built for Practitioners, Not Slides</h3>
              <ul className="space-y-1 flex-1">
                {['Live fire ranges & lab VMs, not slideware', 'Real attack chains from real incidents', 'Small cohorts — direct instructor access'].map(f => (
                  <li key={f} className="flex items-center gap-2 font-mono text-sm text-[var(--text-muted)]">
                    <span className="text-[var(--primary)]">▸</span> {f}
                  </li>
                ))}
              </ul>
              <p className="font-mono text-[var(--text-muted)] text-sm mt-auto pt-2">
                Dates <span className="text-[var(--text-main)] font-bold">{EVENT_DATE_LABEL}</span>
              </p>
            </div>

            {/* Trainings badge */}
            <div className="border-2 border-[var(--primary)] p-5 bg-[var(--surface-highlight)] flex flex-col items-center justify-center gap-3 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--primary)] text-black font-mono text-xs font-bold px-3 py-1 uppercase tracking-widest whitespace-nowrap">
                Hands-On Labs
              </div>
              <Zap className="w-8 h-8 text-[var(--primary)]" />
              <p className="font-mono text-[var(--text-muted)] text-sm text-center">3-Day, 2-Day & 1-Day Trainings</p>
              <div className="text-center flex items-center gap-3">
                <div>
                  <p className="font-mono text-2xl font-bold text-[var(--primary)]">{threeDayTrainings.length}</p>
                  <p className="font-mono text-[10px] text-[var(--text-muted)] mt-1 uppercase tracking-wider">3-Day</p>
                </div>
                <span className="font-mono text-xl text-[var(--text-muted)]">+</span>
                <div>
                  <p className="font-mono text-2xl font-bold text-[var(--primary)]">{twoDayTrainings.length}</p>
                  <p className="font-mono text-[10px] text-[var(--text-muted)] mt-1 uppercase tracking-wider">2-Day</p>
                </div>
                <span className="font-mono text-xl text-[var(--text-muted)]">+</span>
                <div>
                  <p className="font-mono text-2xl font-bold text-[var(--primary)]">{oneDayTrainings.length}</p>
                  <p className="font-mono text-[10px] text-[var(--text-muted)] mt-1 uppercase tracking-wider">1-Day</p>
                </div>
              </div>
              <Link
                to="/services/quarterly-trainings"
                className="w-full bg-[var(--primary)] text-black font-mono font-bold text-sm py-2 text-center uppercase tracking-widest hover:opacity-90 transition-opacity mt-2"
              >
                Browse Trainings
              </Link>
            </div>

            {/* Instructors card */}
            <div className="border border-[var(--border)] p-5 bg-[var(--background)] flex flex-col gap-3">
              <p className="font-mono text-[var(--text-muted)] text-xs uppercase tracking-widest">Instructors</p>
              <h3 className="font-mono font-bold text-[var(--text-main)] text-lg">Practitioners Who Ship, Not Just Teach</h3>
              <ul className="space-y-1 flex-1">
                {['Working security engineers & red teamers', 'Real production & red-team experience, not just theory', 'Conference speakers: Black Hat, DEF CON, Nullcon'].map(f => (
                  <li key={f} className="flex items-center gap-2 font-mono text-sm text-[var(--text-muted)]">
                    <span className="text-[var(--text-muted)]">▸</span> {f}
                  </li>
                ))}
              </ul>
              <p className="font-mono text-[var(--text-muted)] text-sm mt-auto pt-2">Speakers at Black Hat, DEF CON, Nullcon & more</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-3">
            <CountdownTimer targetDate={EARLY_BIRD_DEADLINE} label="Early Bird Pricing Ends In" variant="inline" />
            <Link to="/services/quarterly-trainings/register" className="font-mono text-sm text-[var(--primary)] hover:underline">
              Lock in your seat →
            </Link>
          </div>
        </div>
      </section>

      {/* Meet Your Trainers Section */}
      <section className="py-20 border-t border-[var(--border)]">
        <div className="container-main">
          <div className="text-center mb-12">
            <p className="font-mono text-[var(--primary)] text-base uppercase tracking-widest mb-4">
              ./the_instructors
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[var(--text-main)]">
              Meet Your Trainers
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {trainerStrip.map((instructor, idx) => (
              <Link
                key={idx}
                to={`/services/quarterly-trainings/${instructor.trainingId}`}
                className="group flex gap-4 border border-[var(--border)] hover:border-[var(--primary)] p-5 transition-colors"
              >
                <div className="w-16 h-16 rounded-full border-2 border-[var(--border)] group-hover:border-[var(--primary)] overflow-hidden bg-[var(--surface-highlight)] flex-shrink-0 transition-colors">
                  {instructor.photo
                    ? <img src={instructor.photo} alt={instructor.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    : <div className="w-full h-full flex items-center justify-center font-mono text-[var(--primary)] text-sm font-bold">
                        {instructor.name.split(' ').map(n => n[0]).join('')}
                      </div>
                  }
                </div>
                <div className="font-mono min-w-0">
                  <p className="text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors font-bold">{instructor.name}</p>
                  {(instructor.designation || instructor.company) && (
                    <p className="text-xs text-[var(--text-muted)] mt-0.5">
                      {instructor.designation && instructor.company
                        ? `${instructor.designation} @ ${instructor.company}`
                        : instructor.designation || instructor.company}
                    </p>
                  )}
                  {instructor.knownFor && (
                    <p className="text-xs text-[var(--text-muted)] mt-2 leading-relaxed">{instructor.knownFor}</p>
                  )}
                  <p className="text-xs text-[var(--primary)] mt-2">View training →</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 border-t border-[var(--border)]" data-testid="testimonials-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <p className="font-mono text-[var(--primary)] text-base uppercase tracking-widest mb-4">
              ./from_past_cohorts
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[var(--text-main)]">
              What Attendees Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="testimonial-card p-8 animate-fade-in-up"
                style={{ animationDelay: `${index * 150}ms` }}
                data-testid={`testimonial-${index}`}
              >
                <p className="text-[var(--text-main)] leading-relaxed mb-6">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[var(--surface-highlight)] border border-[var(--primary)] flex items-center justify-center font-mono text-[var(--primary)] font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="text-[var(--text-main)] font-mono font-bold">{testimonial.name}</p>
                    <p className="text-[var(--text-muted)] text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* From Past Events Gallery */}
          <div className="max-w-5xl mx-auto mt-12 overflow-hidden">
            <div className="photo-ticker-track">
              {[0, 1].map(i => (
                <div key={i} className="flex items-center gap-4 pr-4 flex-shrink-0">
                  {[
                    { src: '/images/gallery/june2026-cohort-group.jpeg', alt: 'Byt3con Security Training, June 2026 cohort group photo' },
                    { src: '/images/gallery/june2026-group-supply-chain-village.jpeg', alt: 'Byt3con June 2026 attendees at Supply Chain Village' },
                    { src: '/images/gallery/june2026-signed-banner.jpeg', alt: 'Signed Byt3con Security Training June 2026 banner with attendee notes' },
                    { src: '/images/gallery/online-training-session-1.jpeg', alt: 'Byt3con online training session, live class view' },
                    { src: '/images/gallery/online-training-session-2.jpeg', alt: 'Byt3con online training session, instructor call' },
                  ].map((photo, idx) => (
                    <div key={idx} className="w-[26rem] h-[22rem] flex-shrink-0 border border-[var(--border)] bg-[var(--surface-highlight)] overflow-hidden flex items-center justify-center">
                      <img src={photo.src} alt={photo.alt} className="w-full h-full object-contain" loading="lazy" decoding="async" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Hackers Section */}
      <section className="py-12 md:py-20" data-testid="team-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="font-mono text-[var(--primary)] text-base uppercase tracking-wider mb-4">
              ./the_team
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)]">
              Meet the Team
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-[var(--surface)] border border-[var(--border)] p-8 group hover:border-[var(--primary)] transition-colors duration-300"
                data-testid={`team-member-${index}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src={member.avatar} alt={member.name} className="w-16 h-16 rounded-xl object-cover" loading="lazy" decoding="async" width="64" height="64" />
                  <div>
                    <h3 className="font-mono text-xl font-bold text-[var(--text-main)] mb-1">
                      {member.name}
                    </h3>
                    <p className="text-[var(--primary)] text-sm font-mono">
                      {member.role}
                    </p>
                  </div>
                </div>

                <p className="text-[var(--text-muted)] text-sm leading-relaxed mb-6">
                  {member.bio}
                </p>

                {/* Social */}
                <div className="flex gap-3">
                  <a
                    href={member.social.twitter}
                    className="w-8 h-8 border border-[var(--border)] flex items-center justify-center text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors duration-200"
                    aria-label={`${member.name} Twitter`}
                  >
                    <Twitter className="w-4 h-4" />
                  </a>
                  <a
                    href={member.social.linkedin}
                    className="w-8 h-8 border border-[var(--border)] flex items-center justify-center text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors duration-200"
                    aria-label={`${member.name} LinkedIn`}
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                  {member.social.github && member.social.github !== '#' && (
                    <a
                      href={member.social.github}
                      className="w-8 h-8 border border-[var(--border)] flex items-center justify-center text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors duration-200"
                      aria-label={`${member.name} GitHub`}
                    >
                      <Github className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Strategic Partners Section */}
      <section className="py-20 border-t border-[var(--border)]" data-testid="strategic-partners-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
              Strategic Partners
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[var(--text-main)]">
              Our Strategic Partners
            </h2>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
            {strategicPartners.map((partner, index) =>
              partner.website ? (
                <a key={index} href={partner.website} target="_blank" rel="noopener noreferrer" title={partner.name}>
                  <img src={partner.logo} alt={partner.name} className="h-20 md:h-24 w-auto object-contain hover:opacity-80 transition-opacity duration-300" loading="lazy" decoding="async" />
                </a>
              ) : (
                <img key={index} src={partner.logo} alt={partner.name} title={partner.name} className="h-20 md:h-24 w-auto object-contain" loading="lazy" decoding="async" />
              )
            )}
          </div>
        </div>
      </section>

      {/* Hero Section - COMMENTED OUT */}
      {false && (
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
        {/* Background Effects */}
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-1/4 -left-64" />
        <div className="hero-glow bottom-1/4 -right-64" />

        <div className="container-main relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              {/* <div className="inline-flex items-center gap-2 bg-[var(--surface)] border border-[var(--border)] px-4 py-2">
                <span className="w-2 h-2 bg-[var(--primary)] rounded-full animate-pulse" />
                <span className="font-mono text-sm text-[var(--text-muted)]">System Active</span>
              </div> */}

              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-[var(--text-main)]">
                {typedText}
              </h1>

              <p className="text-lg md:text-xl text-[var(--text-muted)] leading-relaxed max-w-xl">
                Master the art of cybersecurity with hands-on training,
                expert-led courses, and real-world hacking scenarios.
                <span className="text-[var(--primary)]"> Level up your skills.</span>
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/services"
                  className="bg-[var(--primary)] text-black font-mono font-bold px-8 py-4 text-center uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)] hover:shadow-[0_0_25px_var(--glow-primary)] glitch-hover"
                  data-testid="hero-explore-services"
                >
                  Explore Services
                </Link>
                <Link
                  to="/contact"
                  className="border border-[var(--primary)] text-[var(--primary)] font-mono px-8 py-4 text-center uppercase tracking-widest hover:bg-[var(--primary)]/10 transition-colors duration-200"
                  data-testid="hero-contact">
                  Contact Us
                </Link>
              </div>
            </div>

            {/* Right Content - Terminal Window */}
            <div className="terminal-window shadow-2xl animate-fade-in-up" data-testid="terminal-window">
              <div className="terminal-header">
                <div className="terminal-dot red" />
                <div className="terminal-dot yellow" />
                <div className="terminal-dot green" />
                <span className="ml-4 font-mono text-sm text-[var(--text-muted)]">root@byt3con:~</span>
              </div>
              <div className="terminal-body text-sm space-y-3">
                <p style={{ fontSize: '17px' }} className="text-[var(--primary)]">
                  <span className="text-[var(--text-muted)]">$</span> Welcome to Byt3con Academy
                </p>
                <p style={{ fontSize: '17px' }} className="text-[var(--text-muted)]">A practitioner-led Training Academy featuring</p>
                {terminalServices.map((service, index) => (
                  <p style={{ fontSize: '17px' }}
                    key={index}
                    className="text-[var(--primary)] transition-all duration-200"
                    onMouseEnter={() => setHoveredService(index)}
                    onMouseLeave={() => setHoveredService(null)}
                  >
                    [<span className={hoveredService === index ? "text-[var(--accent-cyan)]" : "text-[var(--text-muted)]"}>
                      {hoveredService === index ? '*' : ' '}
                    </span>]
                    <Link
                      to={service.path}
                      className="text-[var(--primary)] underline hover:text-[var(--accent-cyan)] transition-colors duration-200"
                    >
                      {service.label}
                    </Link>
                  </p>
                ))}
                <p style={{ fontSize: '17px' }} className="text-[var(--text-muted)] mt-4">
                  <span className="text-[var(--primary)]">Select item.</span> To begin your journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Stats Section */}
      {/* <section className="py-20  border-y border-[var(--border)]" data-testid="stats-section">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`stat-${index}`}
              >
                <div className="stat-number">{stat.number}</div>
                <div className="text-[var(--text-muted)] font-mono text-sm uppercase tracking-wider mt-2">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* TO RESTORE SERVICES SECTION: See backup comment below
      const servicesSection = (
        <section className="py-24 md:py-32" data-testid="services-section">
          <div className="container-main">
            <div className="max-w-2xl mb-16">
              <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
                Our Services
              </p>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)] mb-6">
                Comprehensive Security Training
              </h2>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed">
                From beginner-friendly courses to advanced attack simulations,
                we have everything you need to build a career in cybersecurity.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <ServiceCard key={index} {...service} index={index} />
              ))}
            </div>
            <div className="mt-12 text-center">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 font-mono text-[var(--primary)] hover:underline"
                data-testid="view-all-services"
              >
                View All Services <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      );
      */}

      {/* TO RESTORE PODCAST SECTION: See backup comment below
      const podcastSection = (
        <section className="py-24 md:py-32 border-y border-[var(--border)]" data-testid="podcast-section">
          <div className="container-main">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="scanline-overlay rounded-lg overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1629555256489-3af0ebcfecef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxwb2RjYXN0JTIwbWljcm9waG9uZSUyMHN0dWRpbyUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3NzAwNjEzOTh8MA&ixlib=rb-4.1.0&q=85"
                    alt="Byt3cast Podcast"
                    className="w-full h-auto object-cover opacity-80"
                  />
                </div>
                <div className="absolute bottom-8 left-8 right-8 bg-black/80 backdrop-blur-sm p-4 border border-[var(--border)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-[var(--primary)] text-sm">Now Playing</span>
                    <div className="waveform">
                      {[...Array(20)].map((_, i) => (
                        <div
                          key={i}
                          className="waveform-bar"
                          style={{
                            height: `${Math.random() * 30 + 10}px`,
                            animationDelay: `${i * 0.1}s`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-[var(--text-main)] text-sm">Ep.47: Breaking into Bug Bounties</p>
                </div>
              </div>
              <div className="space-y-6">
                <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider">
                  Byt3cast Podcast
                </p>
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)]">
                  Watch, Listen & Learn
                </h2>
                <p className="text-[var(--text-muted)] text-lg leading-relaxed">
                  Every week we dive deep into cybersecurity topics with industry experts,
                  ethical hackers, and security researchers. From Tool walkthroughs to 0day discussions to career advice,
                  Byt3cast has you covered.
                </p>
                <ul className="space-y-3">
                  {['Weekly Episodes', 'Expert Interviews', '0Day Discussions', 'Industry News'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-[var(--text-muted)]">
                      <ChevronRight className="w-5 h-5 text-[var(--primary)]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://www.youtube.com/@Byt3conAcademy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block border border-[var(--primary)] text-[var(--primary)] font-mono px-8 py-4 uppercase tracking-widest hover:bg-[var(--primary)]/10 transition-colors duration-200"
                  data-testid="listen-now-btn"
                >
                  Listen Now
                </a>
              </div>
            </div>
          </div>
        </section>
      );
      */}

      {/* FAQ Section - COMMENTED OUT
      <section className="py-24 md:py-32  border-t border-[var(--border)]" data-testid="faq-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
                FAQ
              </p>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)]">
                Frequently Asked Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-[var(--border)] bg-[var(--background)] px-6"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="font-mono text-[var(--text-main)] hover:text-[var(--primary)] text-left py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[var(--text-muted)] pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      */}

      {/* CTA Section - COMMENTED OUT */}
      {false && (
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="hero-glow top-0 left-1/2 -translate-x-1/2" />

        <div className="container-main relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)] mb-6">
              Ready to <span className="text-[var(--primary)]">Level Up</span>?
            </h2>
            <p className="text-[var(--text-muted)] text-lg leading-relaxed mb-10">
              Join thousands of students who have transformed their careers
              with Byt3con Academy. Start your cybersecurity journey today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="bg-[var(--primary)] text-black font-mono font-bold px-10 py-4 uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_20px_var(--glow-primary)] hover:shadow-[0_0_35px_var(--glow-primary)]"
                data-testid="cta-start-learning"
              >
                Start Learning
              </Link>
              <Link
                to="/contact"
                className="border border-[var(--primary)] text-[var(--primary)] font-mono px-10 py-4 uppercase tracking-widest hover:bg-[var(--primary)]/10 transition-colors duration-200"
                data-testid="cta-talk-to-us"
              >
                Talk to Us
              </Link>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Community Partners Section */}
      <section className="py-20 border-t border-[var(--border)]" data-testid="community-partners-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
              Community Partners
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[var(--text-main)]">
              Our Community
            </h2>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
            {communityPartners.map((partner, index) =>
              partner.website ? (
                <a key={index} href={partner.website} target="_blank" rel="noopener noreferrer" title={partner.name}>
                  <img src={partner.logo} alt={partner.name} className="h-16 md:h-20 w-auto object-contain hover:opacity-80 transition-opacity duration-300" loading="lazy" decoding="async" />
                </a>
              ) : (
                <img key={index} src={partner.logo} alt={partner.name} title={partner.name} className="h-16 md:h-20 w-auto object-contain" loading="lazy" decoding="async" />
              )
            )}
          </div>
        </div>
      </section>

      <MobileStickyCTA />
    </div>
  );
};

export default HomePage;
